import {
  has,
  get,
  find,
  pick,
  invoke,
  isEmpty,
  isNumber,
  isNaN,
  isString,
  isFunction,
  isUndefined,
  noop,
  keys,
  values,
} from 'lodash';
import * as jsotp from 'jsotp';
import * as base32 from 'base32.js';
import SingleSurvey from './components/singleSurvey';
import Survey from './components/survey';
import {
  toUTF8Array,
  cleanAnswersFromUnusedProperties,
  escapeJSONSpecialChars,
  transformValuesToString,
  transformConceptsForBackendStorage,
  base64,
} from './utils';
import { getBrandedSurveyFromParams } from './ui-functions';
import request, { getRequestDataAsUrlParams } from './request';

import { KnowledgeKeys } from './logic';
import {
  ANSWER_OTHER,
  DISQUALIFICATION_USER_UNDERAGE,
  EXPECTED_PARTICIPATION_STATUS_ENUMS,
  PARTICIPATION_STATUS,
  MAX_PARTICIPATION_WAIT_SCREENOUT_MILLISECONDS,
  MAX_PARTICIPATION_WAIT_SUCCESS_MILLISECONDS,
  EXPECTED_PAYLOAD_STATUS_ENUMS,
  PENDING_PARTICIPATION_STATUSES,
  IGNORED_UNHANDLED_PROMISE_REJECTION_REASONS,
  DISTRIBUTION_LINK_ENDPAGE_BUTTON_TYPES,
  DISTRIBUTION_LINK_ENDPAGE_TYPES,
  IGNORED_UNHANDLED_PROMISE_REJECTION_CORS,
  IGNORED_UNHANDLED_PROMISE_REJECTION_CORS_WEBSITES,
  IGNORED_UNHANDLED_PROMISE_REJECTION_BY_EXTENSION,
  PORTS,
} from './constants';

// holds all data from survey data like questions, answers to logic principles
// and ui information etc.
Pollfish.helper = (function helper() {
  // retrieve GET url parameter by name
  /*eslint-disable */
  const getURLParameter = (name) =>
    decodeURI(
      (new RegExp(name + '=' + '(.+?)(&|$)').exec(location.search) || [
        ,
        null,
      ])[1],
    );
  /* eslint-enable */

  return {
    // object holding all data from server
    json: {},

    // questions object for faster access
    questions: {},

    // object holding time it took to complete an answer
    ttc: {},

    // prefix for api for assets etc
    prefix: '',

    expiredSurveys: [],

    getURLParameter,

    flag: {
      submitBtnStatus: true,
      invalidDate: false,
      showScrollDownMsg: true,
      keyboardStatus: true,
      loadQuestion: true,
    },

    noSurveyAvailable: false,

    // check if this is in development browser environment
    isDevelopment() {
      if (getURLParameter('isDev') === 'true') {
        return true;
      }
      return false;
    },

    isAndroid() {
      const version = get(Pollfish, 'helper.json.device_info.version');
      return (
        !Pollfish.helper.isHtmlOnly &&
        !Pollfish.helper.isPreview &&
        version === 0
      );
    },

    isIOS() {
      const version = get(Pollfish, 'helper.json.device_info.version');
      return (
        !Pollfish.helper.isHtmlOnly &&
        !Pollfish.helper.isPreview &&
        version === 1
      );
    },

    // the isIOS above only works for iOS devices coming from the SDK.
    // this function is expected to be used when we want to broadly target all iOS devices
    hasIOSNavigator() {
      if (navigator && navigator.userAgent) {
        return /iP(ad|hone|od)/.test(navigator.userAgent);
      }
      // iPad Pro doesn't return "iPad" as it's platform or userAgent
      // solution comes from https://stackoverflow.com/questions/57776001/how-to-detect-ipad-pro-as-ipad-using-javascript
      return (
        navigator.maxTouchPoints &&
        navigator.maxTouchPoints > 2 &&
        /Macintosh/.test(navigator.userAgent)
      );
    },

    isInsideIframe() {
      try {
        if (window.top === window.self) {
          return false;
        }
        return true;
      } catch (e) {
        return true;
      }
    },

    // there are issues when injecting our page inside an iframe
    // (what might be done if someone is using the API and injecting it onto an iframe)
    shouldEnableIOSScrollFixes() {
      // we should not enable scroll fixes anymore outside of iframes
      if (!Pollfish.helper.isInsideIframe()) {
        return false;
      }

      return Pollfish.helper.hasIOSNavigator();
    },

    isAPI() {
      return Pollfish.helper.isHtmlOnly && !Pollfish.helper.isPreview;
    },

    isDebugMode() {
      const debugValue = get(Pollfish, 'helper.json.debug', '');
      // in case we got the debug param as a string or a boolean (as expected)
      if (debugValue === 'true' || debugValue === true) {
        return true;
      }
      return false;
    },

    isCypressEnv() {
      return Pollfish.helper.getURLParameter('env') === 'cypress';
    },

    isRouterDisabled() {
      return Pollfish.helper.json.isRouterDisabled || false;
    },

    isSingleSurvey() {
      return true;
    },

    getIntegrationType() {
      if (Pollfish.helper.isSingleSurvey()) return 'SingleSurvey';
      return 'Unknown';
    },

    isBrandedSurveyFromParams() {
      const surveyThemeJson = getBrandedSurveyFromParams();
      return Object.keys(surveyThemeJson).some((key) => !!surveyThemeJson[key]);
    },

    getSurveyThemeWithSettings() {
      const surveyThemeFromFn = invoke(Pollfish, 'survey.getSurveyTheme');

      const surveyTheme = get(Pollfish, 'helper.json.surveyTheme');

      return surveyThemeFromFn || surveyTheme;
    },

    getSurveyTheme() {
      if (
        Pollfish.helper.isPreview &&
        Pollfish.helper.isBrandedSurveyFromParams()
      ) {
        return getBrandedSurveyFromParams();
      }

      const surveyThemeWithSettings = Pollfish.helper.getSurveyThemeWithSettings();
      return get(surveyThemeWithSettings, 'settings', null);
    },

    hasSurveyTheme() {
      const surveyTheme = Pollfish.helper.getSurveyTheme();
      return !!surveyTheme;
    },

    hasPollfishSurveyTheme() {
      const surveyTheme = Pollfish.helper.getSurveyThemeWithSettings();
      const isPollfishTheme = get(surveyTheme, 'isPollfish', false);
      return !!surveyTheme && isPollfishTheme;
    },

    hasCustomSurveyTheme() {
      return (
        Pollfish.helper.hasSurveyTheme() &&
        !Pollfish.helper.hasPollfishSurveyTheme()
      );
    },

    // helper function that improves readability and used originally to determine overriding the theme with a
    // publisher brand theme
    hasPollfishSurveyNonDefaultTheme() {
      const surveyTheme = Pollfish.helper.getSurveyThemeWithSettings();

      const isDefault = get(surveyTheme, 'isDefault', false);
      return (
        surveyTheme && Pollfish.helper.hasPollfishSurveyTheme() && !isDefault
      );
    },

    isDistributionLink() {
      const isSurveyDistributionLink = invoke(
        Pollfish,
        'survey.isDistributionLink',
      );
      const hasDistributionLinkProperty = get(
        Pollfish,
        'helper.json.is_distribution_link',
      );
      // helper attribute might be empty but survey may be a distribution link survey
      return Boolean(isSurveyDistributionLink || hasDistributionLinkProperty);
    },

    // currently customEndMessage is being user only for distribution link
    getCustomEndMessage() {
      const surveyCustomEndMessage = invoke(
        Pollfish,
        'survey.getCustomEndMessage',
      );
      const helperCustomEndMessage = get(
        Pollfish,
        'helper.json.custom_end_message',
      );
      // helper attribute might be empty
      // but survey may have a distribution link survey custom message
      const customEndMessage = surveyCustomEndMessage || helperCustomEndMessage;
      if (
        Pollfish.helper.isDistributionLink() &&
        Pollfish.helper.json.containsSurvey
      ) {
        return customEndMessage;
      }
      return '';
    },

    getApiType() {
      // these are values expected by the backend as is
      if (Pollfish.helper.isDistributionLink()) {
        return 'distributionSurvey';
      }

      return 'survey';
    },

    // We decided to change the way we send data over to android sdk's
    // for version 22 onwards. We will be posting JSON as strings in the bridge
    // endpoints, to support extendability in the future. iOS version 19 and onwards
    // was working as expected because of WKWebview postMessages.
    androidSupportsJSONParams() {
      return Pollfish.helper.json.sdk_version >= 22;
    },

    getDeviceType() {
      if (Pollfish.helper.isAPI()) return 'API';
      if (Pollfish.helper.isPreview) return 'Preview';
      return 'SDK';
    },

    getNextCount() {
      let { htop } = Pollfish.helper;
      const device = get(Pollfish, 'helper.json.raw') || {};
      if (!device.device_id || !device.api_key) {
        return undefined;
      }
      if (!htop) {
        const cryptoText = `${device.device_id}:${device.api_key}`;
        const binaryKey = toUTF8Array(cryptoText);
        const encoder = new base32.Encoder();
        const encodedString = encoder.write(binaryKey).finalize();
        htop = jsotp.HOTP(encodedString);
        Pollfish.helper.htop = htop;
        Pollfish.helper.requestCount = 0;
      }

      Pollfish.helper.requestCount += 1;
      return htop.at(Pollfish.helper.requestCount);
    },

    getBaseRequestURL() {
      // used by development and cypress envs
      if (Pollfish.helper.baseRequestURL) {
        return Pollfish.helper.baseRequestURL;
      }

      const requestURL = get(Pollfish, 'helper.json.device_info.host');
      if (requestURL && requestURL.indexOf('pollfish.com') >= 0) {
        // NOTE: trying to make requests from an https domain to http will result in
        //       (blocked:mixed-content errors)
        return requestURL.replace(/^http:/, 'https:');
      }
      if (requestURL) {
        return requestURL;
      }
      return `${window.location.protocol}//${window.location.host}`;
    },

    getRedirectData() {
      const device = get(Pollfish, 'helper.json.raw') || {};
      const rewardConversionHash = device.reward_conversion_hash
        ? device.reward_conversion_hash
        : '';
      const data = {
        api_key: device.api_key,
        device_id: device.device_id,
        timestamp: Date.now(),
        encryption: 'NONE',
        version: device.version,
        app_api_key: device.app_api_key,
        request_uuid: device.request_uuid,
        click_id: device.click_id,
        os: device.os,
        ip: device.ip,
        reward_name: device.reward_name ? device.reward_name : '',
        reward_conversion: device.reward_conversion,
        // do not URL encode this property. It will be encoded by the ajax request due to the fact
        // that .request method gets an object instead of a string as a data param
        reward_conversion_hash: rewardConversionHash,
        offerwall: 'false',
        apiType: Pollfish.helper.getApiType(),
        debug: Pollfish.helper.isDebugMode(),
        always_return_content: 'true',
        redirectURL: Pollfish.helper.getBaseRequestURL(),
        lastSurveyId: Pollfish.helper.json.s_id,
        session_id: Pollfish.helper.json.session_id,
      };
      if (device.placement_key) {
        data.placement_key = device.placement_key;
      }
      if (Pollfish.survey) {
        data.survey_session_id = Pollfish.survey.getSurveySessionId();
        data.language = Pollfish.survey.getSurveyLanguage();
      }

      const surveyRevisionId =
        invoke(Pollfish, 'survey.getSurveyRevisionId') || null;
      if (surveyRevisionId) {
        data.survey_revision_id = surveyRevisionId;
      }

      if (device.click_id) {
        data.click_id = device.click_id;
      }
      return data;
    },

    getSurveyRewardData() {
      const json = pick(Pollfish.helper.json, [
        'survey_price',
        'reward_name',
        'reward_value',
        'survey_class',
        'survey_loi',
        'survey_ir',
      ]);

      json.reward_name = json.reward_name
        ? encodeURIComponent(json.reward_name)
        : '';

      return json;
    },

    getSignature() {
      return get(Pollfish, 'helper.json.sig');
    },

    getProviderFromSurveyClass(surveyClass) {
      if (!surveyClass) {
        return null;
      }
      // https://www.pollfish.com/docs/api-documentation
      const parts = surveyClass.split('/');
      const provider = invoke(parts[0], 'toLowerCase');
      if (provider !== 'pollfish') {
        return provider;
      }

      return null;
    },

    addTranslation({ lang, texts, feedbackQuestions }) {
      if (isEmpty(texts)) {
        return;
      }
      Pollfish.helper.json.translations[lang] = texts;
      if (feedbackQuestions) {
        Pollfish.helper.json.feedback_questions[lang] = feedbackQuestions;
      }
    },

    changeLanguage(lang) {
      const translations = get(
        Pollfish,
        `helper.json.translations[${lang}]`,
        null,
      );
      const feedbackQuestions = get(
        Pollfish,
        `helper.json.feedback_questions[${lang}]`,
        null,
      );
      if (translations) {
        Pollfish.ui.texts = translations;
        Pollfish.ui.previousLang =
          Pollfish.ui.currentLang || Pollfish.helper.json.lang;
        Pollfish.helper.json.user_lang = lang;
        Pollfish.ui.currentLang = lang;
      } else {
        // eslint-disable-next-line
        console.error('Translation texts not found');
        Raven.captureException('Translation texts not found', {
          extra: {
            survey_id: Pollfish.helper.json.s_id,
            surveyLang: Pollfish.helper.json.lang,
            userLang: Pollfish.helper.json.user_lang,
            currentLang: Pollfish.ui.currentLang,
            expectedLang: lang,
            currentLangs: Object.keys(
              get(Pollfish, 'helper.json.translations'),
            ),
            initObject: {
              ...Pollfish.helper.json,
            },
          },
        });
      }

      Pollfish.helper.toggleRTL();

      if (feedbackQuestions) {
        Pollfish.helper.feedbackQuestions = feedbackQuestions;
      }
    },

    getNumericValue(refId) {
      const value = $.trim($(`#freeTextNumerical-${refId}`).val());
      if (!isNumber(value)) {
        // we are fine with returning NaN
        return parseFloat(value);
      }
      return value;
    },

    resetTranslations() {
      if (Pollfish.ui.previousLang) {
        Pollfish.helper.changeLanguage(Pollfish.ui.previousLang);
        Pollfish.ui.previousLang = null;
        return;
      }

      // add user_lang as a fallback
      Pollfish.helper.changeLanguage(Pollfish.helper.json.user_lang);
    },

    initErrorHandling() {
      // track all errors
      window.onunhandledrejection = (event) => {
        console.log(event); // eslint-disable-line
        const errorMessage = get(event, 'reason.message', '');
        const errorStack = get(event, 'reason.stack', '');
        const referer = get(document, 'referrer', '');
        // we should not propagate the error if it is an ignored one
        if (
          IGNORED_UNHANDLED_PROMISE_REJECTION_REASONS.indexOf(errorMessage) > -1
        ) {
          return;
        }
        // we should not propagate the error if it comes from specific websites
        if (
          IGNORED_UNHANDLED_PROMISE_REJECTION_CORS.indexOf(errorMessage) > -1 &&
          IGNORED_UNHANDLED_PROMISE_REJECTION_CORS_WEBSITES.indexOf(referer) >
            -1
        ) {
          return;
        }
        // we should not propagate the error if it comes from specific extensions
        if (
          IGNORED_UNHANDLED_PROMISE_REJECTION_BY_EXTENSION.indexOf(errorStack) >
          -1
        ) {
          return;
        }
        // promises return a reason
        // https://developer.mozilla.org/en-US/docs/Web/API/Window/unhandledrejection_event
        // logging "event" will throw an object containing { isTrusted: true }
        // check https://stackoverflow.com/questions/44815172/log-shows-error-object-istrustedtrue-instead-of-actual-error-data
        const error = JSON.stringify(event, [
          'reason',
          'stack',
          'message',
          'arguments',
          'type',
          'name',
        ]);
        Raven.captureException('unhandled promise rejection', {
          extra: {
            referer,
            error,
            ...Pollfish.helper.getRedirectData(),
          },
        });
        // Pollfish.helper.hasError = true;
        // Pollfish.ui.noSurveyAvailable();
      };
      window.onerror = (error, url, line) => {
        // json object with error related information
        const json = {
          error_: error.toString(),
          url_: encodeURIComponent(url),
          line_: line,
          s_id: Pollfish.helper.json.s_id,
        };

        // in case the getFromServer function is broken and there is no s_id
        // available set it manually or else convert it to String to match
        // backend interface
        if (json.s_id === undefined) {
          json.s_id = 'not available';
        } else {
          json.s_id = json.s_id.toString();
        }

        // in case line is not available
        // set it manually or else convert it to String to match
        // backend interface
        if (json.line === undefined) {
          json.line = 'not available';
        } else {
          json.line = json.line.toString();
        }

        // [RES-1857]
        // for some weird reason, on a question that contains a video
        // if you try viewing it in fullscreen chrome might crash with an error
        // that is not printed in the console
        // https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded
        if (error.indexOf('ResizeObserver loop limit') >= 0) {
          return;
        }

        Pollfish.helper.hasError = true;
        // send error data to server without saving in queue
        // Pollfish.mobile.interface.sendToServer(uri, JSON.stringify(json), false);

        Pollfish.ui.noSurveyAvailable();
      };
    },

    handleError(error, extra) {
      Pollfish.helper.hasError = true;
      Raven.captureException(error, {
        extra: {
          ...Pollfish.helper.getRedirectData(),
          ...extra,
        },
      });
      // eslint-disable-next-line
      console.error(error);
      // defering the call since a Homescreen render runs after we render the survey
      // and results in a wrong ui
      setTimeout(() => {
        Pollfish.ui.noSurveyAvailable();
      }, 0);
    },

    exitFullScreenVideo() {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else if (Pollfish.ui.video.webkitExitFullScreen) {
        Pollfish.ui.video.webkitExitFullScreen();
      }
    },

    // bind element for analytics viewed event
    // and then unbind after first time
    bindElementForAnalytics(element) {
      if (!Pollfish.helper.isAPI() && !Pollfish.helper.isDevelopment()) {
        return;
      }
      $(element).bind(
        'click tap',
        Pollfish.helper.handleBindElementForAnalytics,
      );
    },

    handleBindElementForAnalytics(e) {
      const element = e.currentTarget;
      e.preventDefault();
      // NOTE: In the API case we don't know if a publisher has integrated
      //       the survey in an iframe or not. Therefore we can't make an assumption
      //       as to when a survey was viewed.
      const containsSurvey = get(Pollfish, 'helper.json.containsSurvey');
      const surveyId = get(Pollfish, 'helper.json.s_id');
      const url = '/v2/device/set/survey/viewed';
      const data = `{"s_id": "${surveyId}"}`;
      $(element).unbind(
        'click tap',
        Pollfish.helper.handleBindElementForAnalytics,
      );
      // should not call survey viewed if integration is singleSurvey
      // and there is not a survey returned
      if (Pollfish.helper.isSingleSurvey() && !containsSurvey) {
        return;
      }
      Pollfish.mobile.interface.sendToServer(url, data, false);
    },

    shouldAutofocus() {
      if (Pollfish.helper.isPreview) {
        return false;
      }
      return true;
    },

    hasAcceptedTerms() {
      // has_accepted_terms is becoming true when a user accepts the GDPR page
      return Pollfish.helper.json.has_accepted_terms || false;
    },

    setAcceptedTerms(accept = true) {
      Pollfish.helper.json.has_accepted_terms = accept;
    },

    initSingleSurvey() {
      new SingleSurvey().render();
      Pollfish.store.setIsPreviousPageGDPR(null);
    },

    getErrorObject() {
      return {
        survey_id: Pollfish.helper.json.s_id,
        questions: Pollfish.helper.json.questions,
        debug_questions: Pollfish.helper.debug.questions,
      };
    },

    handlePreviousSurvey(surveyId) {
      Pollfish.ui.showSpinner();
      // do not call again the surveyNoFound OR webviewLoaded events
      // since this is a redirect
      Pollfish.helper.webViewLoadedCalled = true;
      Pollfish.helper.json.raw.lastSurveyId = null;

      Pollfish.helper.getMediationSurveyStatus(surveyId, (participation) => {
        // populate third_party_provider for given s_id from survey_class information
        const thirdPartyProvider = Pollfish.helper.getProviderFromSurveyClass(
          participation.survey_class,
        );
        Pollfish.helper.json.third_party_provider = thirdPartyProvider;

        if (Pollfish.survey) {
          Pollfish.survey.setProps({ thirdPartyProvider });
        }
        // only show feedback survey after verifying that the survey is still pending
        if (participation.status === 'pending') {
          Pollfish.ui.showFeedbackSurvey();
        }
      });
    },

    setupMobileIntegration() {
      // initialize the mobile interface based on the SDK used
      if (!isEmpty(Pollfish.helper.html.json)) {
        if (
          Pollfish.helper.html.json.isAdditionalSurvey ||
          (has(Pollfish, 'helper.html.json.raw.redirectURL') &&
            has(Pollfish, 'helper.html.json.raw.lastSurveyId'))
        ) {
          Pollfish.helper.isRouterRedirect = true;
          Pollfish.mobile.interface.init(Pollfish.mobile.interface.htmlOnly);
          Pollfish.helper.isHtmlOnly = true;
          Pollfish.ui.hideCloseButtonFromHeader();
        } else {
          Pollfish.mobile.interface.init(Pollfish.mobile.interface.htmlOnly);
          Pollfish.helper.isHtmlOnly = true;

          if (getURLParameter('preview') === 'true') {
            Pollfish.helper.isPreview = true;
          }
        }

        Pollfish.helper.prefix = 'https://www.pollfish.com/mobile/';
      } else if (Pollfish.helper.isDevelopment()) {
        Pollfish.mobile.interface.init(Pollfish.mobile.interface.development);
      }
    },

    setupAvailableLanguages() {
      // get the text based on the language of the user
      const userLang = get(Pollfish, 'helper.json.user_lang', null);
      const surveyLang = get(Pollfish, 'helper.json.lang', null);
      const userTranslationTexts = get(
        Pollfish,
        `helper.json.translations[${userLang}]`,
      );
      const surveyTranslationTexts = get(
        Pollfish,
        `helper.json.translations[${surveyLang}]`,
      );
      Pollfish.helper.addTranslation({
        lang: userLang,
        texts: userTranslationTexts,
      });
      Pollfish.helper.addTranslation({
        lang: surveyLang,
        texts: surveyTranslationTexts,
      });
      Pollfish.helper.changeLanguage(surveyLang);
    },

    getUserDetails() {
      return invoke(Pollfish, 'getUserDetails') || {};
    },

    hasAlreadyCompletedMediationSurvey() {
      // handle out of sync update about completing a given survey
      if (Pollfish.helper.json.thirdPartyComplete !== null) {
        if (Pollfish.helper.json.thirdPartyComplete.qualified) {
          // mark survey as completed since all questions have been answered
          Pollfish.mobile.interface.setSurveyCompleted();
        } else {
          Pollfish.mobile.interface.userNotEligible();

          // legacy code TBD in the future
          // this helps to close indicator permanently
          // since closeAndNoShow simply checks if the survey completed in SDK
          if (Pollfish.helper.json.sdk_version <= 5) {
            Pollfish.mobile.interface.setSurveyCompleted();
          }
        }
      }
    },

    handleDistributionLinkEndPage() {
      const distributionLinkRedirectData = get(
        Pollfish,
        'helper.json.distribution_link_redirect_data',
      );

      Pollfish.event.onZoomImage();
      const isDataInvalid =
        !distributionLinkRedirectData ||
        values(distributionLinkRedirectData).some(isUndefined);
      if (isDataInvalid) {
        // entering here means that we received a not valid distribution link redirect data object
        Raven.captureException('Distribution link redirect data object error', {
          extra: {
            containsSurvey: Pollfish.helper.json.containsSurvey,
            distributionLink: Pollfish.helper.isDistributionLink(),
            distribution_link_redirect_data: distributionLinkRedirectData,
          },
        });
        Pollfish.helper.handleSessionError();
        return;
      }

      const {
        redirectOnDemand,
        redirectType,
        url,
        endScreenType,
      } = distributionLinkRedirectData;

      // we need to show the no survey available screen when survey is already closed
      Pollfish.helper.noSurveyAvailable = true;
      if (
        endScreenType === DISTRIBUTION_LINK_ENDPAGE_TYPES.SURVEY_ALREADY_TAKEN
      ) {
        Pollfish.ui.showScreenOut({
          reason: PARTICIPATION_STATUS.SURVEY_ALREADY_TAKEN,
          payload: {
            redirectOnDemand,
            endPageSettingType: redirectType,
          },
        });
      } else if (
        endScreenType === DISTRIBUTION_LINK_ENDPAGE_TYPES.SURVEY_NOT_ACTIVE
      ) {
        Pollfish.ui.showScreenOut({
          reason: PARTICIPATION_STATUS.SURVEY_NOT_ACTIVE,
          payload: {
            redirectOnDemand,
            endPageSettingType: redirectType,
          },
        });
      } else if (
        endScreenType === DISTRIBUTION_LINK_ENDPAGE_TYPES.SURVEY_EXPIRED
      ) {
        switch (redirectType) {
          case DISTRIBUTION_LINK_ENDPAGE_BUTTON_TYPES.DEFAULT:
          case DISTRIBUTION_LINK_ENDPAGE_BUTTON_TYPES.RESULTS:
          case DISTRIBUTION_LINK_ENDPAGE_BUTTON_TYPES.NO_REDIRECT:
            Pollfish.ui.showScreenOut({
              reason: PARTICIPATION_STATUS.SURVEY_EXPIRED,
              onDemandEndPageUrl: url,
              payload: {
                redirectOnDemand,
                endPageSettingType: redirectType,
              },
            });
            break;
          case DISTRIBUTION_LINK_ENDPAGE_BUTTON_TYPES.CUSTOM:
          case DISTRIBUTION_LINK_ENDPAGE_BUTTON_TYPES.PRODEGE_CUSTOM:
            window.location.href = url;
            break;
          default:
            // entering here means that we received a not valid distribution link redirect type
            Raven.captureException('Distribution link redirect type error', {
              extra: {
                containsSurvey: Pollfish.helper.json.containsSurvey,
                distributionLink: Pollfish.helper.isDistributionLink(),
                distribution_link_redirect_data: distributionLinkRedirectData,
              },
            });
            Pollfish.helper.handleSessionError();
        }
      } else {
        switch (redirectType) {
          case DISTRIBUTION_LINK_ENDPAGE_BUTTON_TYPES.DEFAULT:
          case DISTRIBUTION_LINK_ENDPAGE_BUTTON_TYPES.RESULTS:
          case DISTRIBUTION_LINK_ENDPAGE_BUTTON_TYPES.NO_REDIRECT:
            Pollfish.ui.thankYouBasic({
              payload: {
                redirectOnDemand,
                endPageSettingType: redirectType,
              },
            });
            break;
          case DISTRIBUTION_LINK_ENDPAGE_BUTTON_TYPES.CUSTOM:
          case DISTRIBUTION_LINK_ENDPAGE_BUTTON_TYPES.PRODEGE_CUSTOM:
            window.location.href = url;
            break;
          default:
            // entering here means that we received a not valid distribution link redirect type
            Raven.captureException('Distribution link redirect type error', {
              extra: {
                containsSurvey: Pollfish.helper.json.containsSurvey,
                distributionLink: Pollfish.helper.isDistributionLink(),
                distribution_link_redirect_data: distributionLinkRedirectData,
              },
            });
            Pollfish.helper.handleSessionError();
        }
      }
    },

    setupPollfishHelperJson(serverResponse) {
      if (!serverResponse) {
        Pollfish.helper.handleSessionError();
        return;
      }
      try {
        Pollfish.helper.json = JSON.parse(serverResponse);
        // not happening any more, but keeping as an exception handler
        if (!Pollfish.helper.json) {
          Raven.captureException(
            'JSON.parse in get fromServer did not throw but request was empty',
            {
              extra: {
                serverResponse,
                deviceType: Pollfish.helper.getDeviceType(),
              },
            },
          );
        }
      } catch (e) {
        Raven.captureException(e, {
          extra: {
            serverResponse,
            responseType: typeof serverResponse,
            deviceType: Pollfish.helper.getDeviceType(),
          },
        });

        // Sentry has many requests failing with a serverResponse being [object Object].
        if (typeof serverResponse === 'object') {
          throw new Error(
            `getFromServer(). Tried to parse response as json but it was already object ${JSON.stringify(
              serverResponse,
            )}`,
          );
        } else {
          throw new Error(
            `getFromServer(). Tried to parse as JSON: '${serverResponse}'`,
          );
        }
      }
    },

    // object containing methods used on document load
    document: {
      // initialize structures, objects and welcome screens
      init(successCallback) {
        // attach window resize event
        Pollfish.event.windowResize();
        // attach global error handling
        Pollfish.helper.initErrorHandling();
        // attach fastclick on body to solve mobile tap delays
        FastClick.attach(document.body);

        // initialise the mobile interface based on the SDK used
        Pollfish.helper.setupMobileIntegration();

        // bind element for analytics viewed event
        // and then unbind after first time
        Pollfish.helper.bindElementForAnalytics('body');

        // get survey data
        Pollfish.mobile.interface.getFromServer((serverResponse) => {
          Pollfish.helper.setupPollfishHelperJson(serverResponse);

          if (Pollfish.helper.shouldHideLogo()) {
            Pollfish.ui.hideLogo();
          }

          Pollfish.helper.setupAvailableLanguages();
          Pollfish.ui.renderLanguageSelect();
          Pollfish.helper.toggleRTL();

          // used for error reporting and as a hack to "reset" questions back to their original form
          Pollfish.helper.debug = {
            questions: JSON.stringify(Pollfish.helper.json.questions),
          };

          const containsSurvey = get(Pollfish, 'helper.json.containsSurvey');
          const isDistributionLink = Pollfish.helper.isDistributionLink();

          // containing both a last survey id and a redirectURL means we got redirected here
          // and we are initialising mobile to handle the given surveyId
          const lastSurveyId = get(Pollfish, 'helper.json.raw.lastSurveyId');
          const redirectURL = get(Pollfish, 'helper.json.raw.redirectURL');
          if (lastSurveyId && redirectURL) {
            // session id is missing from SDK mediation redirects
            // and we are grabbing it from a different object on the url
            Pollfish.helper.json.session_id = get(
              Pollfish,
              'helper.json.raw.session_id',
            );
            const surveySessionId = get(
              Pollfish,
              'helper.json.raw.survey_session_id',
            );
            const survey = new Survey({
              s_id: lastSurveyId,
              survey_session_id: surveySessionId,
              isPartialSurvey: true,
            });
            Pollfish.survey = survey;
            Pollfish.helper.handlePreviousSurvey(survey.props.surveyId);
            Pollfish.event.close();
            Pollfish.event.onZoomImage();
            successCallback();
            return;
          }
          if (!containsSurvey && isDistributionLink) {
            // not having a survey and being a distribution link means
            // respondent has a url for a survey that is no longer available
            // but we want to see and ending screen
            Pollfish.helper.handleDistributionLinkEndPage();
            successCallback();
            return;
          }
          if (!containsSurvey) {
            Pollfish.ui.noSurveyAvailable();
            Pollfish.event.close();
            Pollfish.event.onZoomImage();
            successCallback();
            return;
          }

          // index_dev.ejs is calling setUserContext earlier. If this function is missing
          // then it is expected that the user has currently no data, so we populate it again
          const userDetails = Pollfish.helper.getUserDetails();
          if (!userDetails) {
            Raven.setUserContext({
              survey_id: Pollfish.helper.json.s_id,
              sdk_version: Pollfish.helper.json.sdk_version,
              device_id: Pollfish.helper.json.raw.device_id,
              api_key: Pollfish.helper.json.raw.api_key,
              placement_key: Pollfish.helper.json.raw.placement_key,
            });
          }

          // force incentive false for preview
          if (Pollfish.helper.isPreview) {
            Pollfish.helper.json.incentive = false;
          }

          Pollfish.helper.hasAlreadyCompletedMediationSurvey();

          // remove the ability to close the panel
          if (Pollfish.helper.shouldHideCloseButton()) {
            // Pollfish.ui.hideCloseButtonFromHomescreen();
            Pollfish.ui.hideCloseButtonFromHeader(); // blame vourkosa for this change :P
          }

          Pollfish.helper.initSingleSurvey();

          if (!Pollfish.helper.isDistributionLink()) {
            // bind no thanks event
            Pollfish.event.noThanks();
            // bind the withdraw consent
            Pollfish.event.withdrawConsent();
          }

          // bind close event
          Pollfish.event.close();
          Pollfish.event.onZoomImage();

          // bind take survey or continue survey event
          Pollfish.event.takeSurvey();

          successCallback();
        });
      },
    },

    // object containing methods used on window load
    window: {
      // visual configuration and send event to sdk
      init() {
        $('.ui-page').css({ visibility: 'visible' });

        Pollfish.ui.applyBrandedSurveyStyles();

        // load analytics before notifying the plugins
        Pollfish.analytics.init();

        const shouldCallWebviewLoaded =
          !Pollfish.helper.isRouterRedirect &&
          !Pollfish.helper.webViewLoadedCalled;

        if (shouldCallWebviewLoaded) {
          Pollfish.mobile.interface.webViewLoaded();
        }
        // TODO: update to add this only when fullscreen
        if (Pollfish.helper.shouldEnableIOSScrollFixes()) {
          $('html,body').addClass('device-iphone');
        }

        if (Pollfish.helper.isDistributionLink()) {
          $('.header__action-close-btn').addClass('hidden');
        }

        if (Pollfish.helper.json.thirdPartyComplete === null) {
          // don't show welcome screen in case it is a
          // survey we use internally to get feedback
          if ([333981, 333994].indexOf(Pollfish.helper.json.s_id) > -1) {
            $('.header__action-close-btn').addClass('hidden');
            $('.js-take-survey-btn').tap();
          }

          // notify server that survey will is received and loaded
          Pollfish.mobile.interface.sendToServer(
            '/v2/device/set/survey/received',
            `{"s_id": "${Pollfish.helper.json.s_id}"}`,
            false,
          );
        }
      },
    },

    getQuestionIndex() {
      return Pollfish.store.answeredQuestions.length + 1;
    },

    toggleRTL() {
      const rtlLanguages = ['ar', 'eg', 'he', 'fa', 'ur'];
      const { currentLang } = Pollfish.ui;
      if (rtlLanguages.indexOf(currentLang) > -1) {
        $('html').attr('dir', 'rtl');
      } else {
        $('html').removeAttr('dir');
      }
    },

    checkTimeElapsed() {
      // calculate elapse time of the current questions
      Pollfish.helper.ttc.elapsed = new Date() - Pollfish.helper.ttc.start;

      if (Pollfish.helper.ttc.elapsed < 1000) {
        Pollfish.analytics.sendEvent(
          'fraud-test',
          'responsesUnderOneSecond',
          'Lightning fast responses (less than 1 second)',
        );
        Pollfish.ui.showSlowDownWarning(Pollfish.helper.ttc.elapsed);
      } else if (Pollfish.helper.ttc.elapsed < 2000) {
        Pollfish.analytics.sendEvent(
          'fraud-test',
          'responsesUnderTwoSeconds',
          'fast responses (less than 2 seconds)',
        );
        Pollfish.ui.showSlowDownWarning(Pollfish.helper.ttc.elapsed);
      } else if (Pollfish.helper.ttc.elapsed < 2500) {
        Pollfish.analytics.sendEvent(
          'fraud-test',
          'responsesUnderTwoPointFiveSeconds',
          'fast-ish responses (less than 2.5 seconds)',
        );
        Pollfish.ui.showSlowDownWarning(Pollfish.helper.ttc.elapsed);
      }
    },

    // request for another survey
    getAdditionalSurvey() {
      // load intermediate screen till another survey is fetched
      Pollfish.ui.loading();

      // @TODO: check delay
      const delay = Pollfish.helper.isCypressEnv() ? 200 : 2000;
      setTimeout(() => {
        Pollfish.helper.requestAdditionalSurveyFromServer();
      }, delay);
    },

    requestAdditionalSurveyFromServer() {
      Raven.captureBreadcrumb({
        message: 'requestAdditionalSurvey',
        category: 'router',
      });
      let baseURL = '';
      // NOTE: @file:// protocol exists when this project is being loaded through the mobile SDK
      if (
        window.location.protocol === 'file:' ||
        window.location.protocol === 'data:'
      ) {
        baseURL = 'https://wss.pollfish.com';
      }

      baseURL = Pollfish.helper.getBaseRequestURL() || baseURL;

      if (Pollfish.helper.isDevelopment() || Pollfish.helper.isCypressEnv()) {
        baseURL = `http://localhost:${PORTS.MOCK}`;
      }

      const jsonString = JSON.stringify({
        ...Pollfish.helper.json.raw,
        session_id: Pollfish.helper.json.session_id,
      });

      const returnAdditionalSurvey =
        Pollfish.helper.json.raw.withAdditionalSurvey !== 'false';
      let url = `${baseURL}/v2/device/register/false/true?dontencrypt=true&json=${encodeURIComponent(
        jsonString,
      )}&returnAdditionalSurvey=${returnAdditionalSurvey}`;

      const sig = Pollfish.helper.getSignature();
      if (sig) {
        url += `&sig=${encodeURIComponent(sig)}`;
      }

      const promise = $.ajax({
        type: 'GET',
        url,
      });

      // we need to clear the survey since we are expecting either a new one or nothing
      Pollfish.survey = undefined;
      promise.then((response, textStatus, xhr) => {
        if (!response || xhr.status === 204) {
          Pollfish.analytics.sendEvent(
            'router',
            'noSurvey',
            'No Additional survey found',
          );
          Pollfish.ui.noSurveyAvailable();
          return;
        }
        Pollfish.analytics.sendEvent(
          'router',
          'receivedSurvey',
          'Received Additional Survey',
        );
        Pollfish.helper.handleAdditionalSurvey(response);
      });

      promise.error((xhr, statusText, errorThrown) => {
        const errorMessage = errorThrown || `${xhr.status} error thrown`;
        Pollfish.analytics.sendEvent(
          'router',
          'error',
          'Error while receiving new survey',
        );
        Raven.captureException(errorMessage, {
          extra: {
            statusText,
            url,
            statusCode: xhr.status,
          },
        });
        Pollfish.ui.noSurveyAvailable();
      });
    },

    handleUserNotEligible() {
      const rawData = get(Pollfish, 'helper.json.raw', {});
      const rewardConversionHash = rawData.reward_conversion_hash || '';
      const surveyId = get(Pollfish, 'helper.json.s_id', '');
      // notify server that user is not eligible
      const requestParams = {
        s_id: surveyId.toString(),
        reward_name: rawData.reward_name
          ? encodeURIComponent(rawData.reward_name)
          : '',
        reward_conversion: rawData.reward_conversion || '',
        reward_conversion_hash: encodeURIComponent(rewardConversionHash),
      };

      request({
        url: '/v2/device/set/survey/noteligible',
        method: 'POST',
        data: requestParams,
      }).catch(noop);

      let disqualificationReason;
      let shouldGetInRouter = false;
      if (
        !Pollfish.helper.isPreview &&
        !Pollfish.helper.isRouterDisabled() &&
        !Pollfish.helper.isDebugMode()
      ) {
        shouldGetInRouter = true;
      }

      Pollfish.helper.handleSurveyComplete({
        isUserNotEligible: true,
        disqualificationReason,
        shouldGetInRouter,
      });
    },

    handleSessionError() {
      // this handling is used when we want to terminate a session completely due
      // to something going wrong
      Pollfish.helper.hasError = true;
      Pollfish.ui.noSurveyAvailable();
    },

    handleGlobalPromiseError(e) {
      if (e) {
        // we sometimes get status code 0 or similar from request method
        // and we don't want to log those since we don't know what to do with them
        // eslint-disable-next-line
        console.error(e);
        Raven.captureException(e);
      }
      // if something goes wrong we will be missing the much needed
      // survey_session_id and the subsequent calls might fail (or just miss critical data)
      Pollfish.helper.handleSessionError();
    },

    async handleUnsuccesfullGroupSetup(error) {
      // eslint-disable-next-line
      console.error('Error while trying to setup groups', error);
      Raven.captureException(error, {
        extra: {
          ...error.cause,
        },
      });
      Pollfish.helper.handleSurveyComplete({
        isUserNotEligible: true,
        disqualificationReason: PARTICIPATION_STATUS.SURVEY_EXPIRED,
        shouldCallSurveyComplete: false,
        // shouldGetInRouter,
      });
    },

    async handleSurveyComplete({
      isUserNotEligible,
      disqualificationReason,
      shouldGetInRouter,
      shouldCallSurveyComplete = true,
    }) {
      const { surveyId } = Pollfish.survey.props;
      // we are handling session finished differently now
      const isSessionFinished = false;
      let maxWait = 0;
      if (isUserNotEligible) {
        maxWait = MAX_PARTICIPATION_WAIT_SCREENOUT_MILLISECONDS;
      } else {
        maxWait = MAX_PARTICIPATION_WAIT_SUCCESS_MILLISECONDS;
      }

      if (Pollfish.helper.isCypressEnv()) {
        maxWait = 0;
      }

      Pollfish.ui.showSpinner();
      let payload;
      try {
        if (shouldCallSurveyComplete) {
          await Pollfish.helper.sendSurveyCompletedToServer({
            isSessionFinished,
            isUserNotEligible,
          });
        }
        payload = await Pollfish.helper.getSurveyStatus({
          surveyId,
          maxWait,
        });
        Pollfish.store.lastPayload = { ...payload, surveyId };
      } catch (e) {
        // we won't have an error object if it is coming from the request since we already handle it
        if (e) {
          Raven.captureException(e);
        }
        // eslint-disable-next-line
        console.error('There was an error while trying to fetch status', e);
        payload = {
          status: 'pending',
          participationStatus: disqualificationReason,
          redirectUrl: Pollfish.helper.json.default_redirect_url,
          // ...Pollfish.helper.getSurveyRewardData(),
        };
      }

      let onDemandEndPageUrl = '';
      // if redirectOnDemand is true that means user will get redirected
      // to custom end page when decides to click the button in end screen,
      // due to some implications in backend we should get immediately the
      // end page url right after participation-status before it expires in backend
      if (payload.redirectOnDemand) {
        try {
          const redirectOnDemandRequestUrl = Pollfish.helper.getOnDemandRedirectURL(
            surveyId,
          );
          const redirectOnDemandData = { s_id: surveyId };
          const onDemandRedirectURLPayload = await request({
            url: redirectOnDemandRequestUrl,
            data: redirectOnDemandData,
          });
          onDemandEndPageUrl = onDemandRedirectURLPayload.endPageUrl;
        } catch (e) {
          // we won't have an error object
          // if it is coming from the request since we already handle it
          if (e) {
            Raven.captureException(e);
          }
          // eslint-disable-next-line
          console.error(
            'There was an error while trying to fetch onDemandEndPageUrl',
            e,
          );
        }
      }

      // if we timeout on a pending status we continue with the expected flow
      const isUserEligible =
        payload.status === 'success' ||
        (payload.status === 'pending' && !isUserNotEligible);
      const isUserScreenedOut =
        payload.status === 'screened_out' ||
        (payload.status === 'pending' && isUserNotEligible);

      const reward = this.getRewardFromParticipation(payload);
      if (isUserEligible) {
        Pollfish.mobile.interface.setSurveyCompleted(payload);

        const shouldGetIntoRouter =
          Pollfish.helper.isSingleSurvey() &&
          !Pollfish.helper.isPreview &&
          !Pollfish.helper.isRouterDisabled();
        if (shouldGetIntoRouter) {
          Pollfish.helper.getAdditionalSurvey();
          return;
        }
        await Pollfish.helper.sendSessionFinishedToServer();

        // We are deferring the call here because there is a race condition with
        // rendering the loading page. This can only create issues when the endpoint
        // responds in 1< ms so only for development
        setTimeout(() => {
          Pollfish.ui.thankYouBasic({
            isResultPending: payload.status === 'pending',
            reward,
            surveyId,
            payload,
            onDemandEndPageUrl,
          });
        }, 0);
      } else if (isUserScreenedOut) {
        // in case of having a redirect url we will be only redirecting
        if (disqualificationReason === DISQUALIFICATION_USER_UNDERAGE) {
          await Pollfish.helper.sendSessionFinishedToServer();
          Pollfish.ui.showScreenOut({
            reason: disqualificationReason,
            payload,
            surveyId,
            onDemandEndPageUrl,
          });
          return;
        }

        if (shouldGetInRouter) {
          Pollfish.helper.getAdditionalSurvey();
          return;
        }

        if (
          payload.participationStatus === PARTICIPATION_STATUS.USER_REJECTION
        ) {
          // even though we are not getting into a router we want to show a different
          // message to the respondent that rejected a survey
          Pollfish.ui.noSurveyAvailable();
          return;
        }

        // ui.noSurveyAvailable calls its own sessionFinished event
        await Pollfish.helper.sendSessionFinishedToServer();

        // legacy code TBD in the future
        // this helps to close indicator permanently
        // since closeAndNoShow simply checks if the survey completed in SDK
        if (Pollfish.helper.json.sdk_version <= 5) {
          Pollfish.mobile.interface.setSurveyCompleted();
        }

        Pollfish.ui.showScreenOut({
          reason: payload.participationStatus || disqualificationReason,
          payload,
          reward,
          surveyId,
          onDemandEndPageUrl,
        });
      } else {
        throw new Error(`Unhandled payload status ${payload.status}`);
      }
    },

    getSurveyCompletedPayload(payload) {
      if (!payload) {
        return undefined;
      }
      const reward = this.getRewardFromParticipation(payload);
      const revenue = this.getRevenueFromParticipation(payload);
      return {
        survey_price: revenue,
        reward_name: reward.name || '',
        reward_value: reward.value,
        survey_class: payload.survey_class,
        survey_loi: payload.survey_loi,
        survey_ir: payload.survey_ir,
      };
    },

    getRevenueFromParticipation(payload) {
      if (!payload) {
        return undefined;
      }
      let publisherRevenue;
      if (Pollfish.helper.isDistributionLink()) {
        // in distribution link there is no publisher
        // so the revenue will be always zero
        publisherRevenue = 0;
      } else {
        // for single survey approaches we always need to show the session
        // since the thank you page is the final screen our respondent will see
        // so we want to also take into account past screen_outs
        // fallbacks to values outside the session.
        publisherRevenue = get(payload, 'session.publisher_revenue');
        // publisherRevenue can also be zero
        if (isUndefined(publisherRevenue)) {
          publisherRevenue = get(payload, 'publisher_revenue');
          if (!PENDING_PARTICIPATION_STATUSES.includes(payload.status)) {
            Raven.captureMessage('Missing session.publisher_revenue', {
              extra: {
                ...Pollfish.helper.getRedirectData(),
                payload,
              },
            });
          }
        }
      }

      if (!publisherRevenue) {
        // we need to always return an string value
        return 0;
      }
      return publisherRevenue;
    },

    getRewardFromParticipation(payload) {
      const rewardsEnabled = get(
        Pollfish,
        'helper.json.reward_settings_enabled',
      );
      if (!rewardsEnabled || !payload) {
        return {};
      }
      // for single survey approaches we always need to show the session
      // since the thank you page is the final screen our respondent will see
      // so we want to also take into account past screen_outs
      // fallbacks to values outside the session.
      const rewardValue =
        get(payload, 'session.reward_value') || get(payload, 'reward_value');
      const rewardName =
        get(payload, 'session.reward_name') || get(payload, 'reward_name');

      // we are expected the rewardValue to be a string in most cases
      // so we only validate it here and let it pass
      const rewardValueFormatted = parseFloat(rewardValue);
      const isValidValue =
        isNumber(rewardValueFormatted) &&
        !isNaN(rewardValueFormatted) &&
        rewardValueFormatted > 0;
      if (!isValidValue) {
        return {};
      }

      return {
        name: rewardName,
        value: rewardValue,
      };
    },
    replacePortWhenDevelopment(url) {
      if (Pollfish.helper.isDevelopment()) {
        // getBaseRequestURL will return `PORTS.MOCK` as the port since that is
        // the dyson API. But for redirects we want to keep the same port as the one we use
        return url.replace(PORTS.MOCK, PORTS.DEV);
      }
      return url;
    },
    getRedirectURL(surveyId) {
      if (!surveyId) {
        Raven.captureMessage(
          'Called getRedirectURL without providing a surveyId or providing a falsy value',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
        return false;
      }
      let baseURL = Pollfish.helper.getBaseRequestURL();
      baseURL = Pollfish.helper.replacePortWhenDevelopment(baseURL);
      const data = { s_id: surveyId };
      const url = `/v2/surveys/${surveyId}/end-page-gateway?${getRequestDataAsUrlParams(
        data,
      )}`;
      const finalURL = `${baseURL}${url}`;
      return finalURL;
    },
    getOnDemandRedirectURL(surveyId) {
      if (!surveyId) {
        Raven.captureMessage(
          'Called getOnDemandRedirectURL without providing a surveyId or providing a falsy value',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
        return false;
      }
      return `/v2/surveys/${surveyId}/end-page-url`;
    },

    handleAdditionalSurvey(responseText) {
      let json;
      let response = responseText;
      try {
        if (isString(responseText)) {
          response = JSON.parse(responseText);
        }
        json = JSON.parse(response.mobile_data);
        if (!json.containsSurvey) {
          Pollfish.analytics.sendEvent(
            'router',
            'noSurvey',
            'No Additional survey found',
          );
          Pollfish.ui.noSurveyAvailable();
          return false;
        }
        if (json.s_id === undefined || json.questions === undefined) {
          throw new Error('no survey id');
        }
      } catch (e) {
        Pollfish.analytics.sendEvent(
          'router',
          'error',
          'Error while receiving new survey',
        );
        Raven.captureException(e, {
          extra: {
            responseText,
          },
        });
        Pollfish.ui.noSurveyAvailable();
        return false;
      }

      // TODO: remove this when the backend responds with feedback_questions
      // it is currently needed for router redirects where new surveys don't contain
      // feedback questions
      if (!json.feedback_questions) {
        json.feedback_questions = Pollfish.helper.json.feedback_questions;
      }

      if (!json.raw) {
        json.raw = Pollfish.helper.json.raw;
      }

      keys(json.translations).forEach((lang) => {
        const translations = json.translations[lang];
        const feedbackQuestions = get(json, `feedback_questions[${lang}]`);
        Pollfish.helper.addTranslation({
          lang,
          texts: translations,
          feedbackQuestions,
        });
      });

      json.translations = Pollfish.helper.json.translations;
      // change the language to the new surveys lang. Will keep current if translations are missing
      Pollfish.helper.changeLanguage(json.lang);

      // TODO: we can ask the backend service to return this boolean
      //       on additional survey requests
      json.reward_settings_enabled =
        Pollfish.helper.json.reward_settings_enabled;
      json.default_redirect_url = Pollfish.helper.json.default_redirect_url;
      // since we just updated the user language through changeLanguage
      // update the json user_lang before set
      json.user_lang = Pollfish.helper.json.user_lang;

      Pollfish.helper.json = json;
      Pollfish.mobile.interface.sendToServer(
        '/v2/device/set/survey/viewed',
        `{"s_id": "${json.s_id}"}`,
        false,
      );
      Pollfish.survey = new Survey(json);
      if (isEmpty(json.questions)) {
        Pollfish.analytics.sendEvent(
          'router',
          'questionsMissing',
          'Received survey with no questions',
        );
        Pollfish.ui.noSurveyAvailable();
        return false;
      }
      Pollfish.survey.start().catch(Pollfish.helper.handleError);
      return true;
    },

    handleSurveyReject() {
      Pollfish.helper.sendSurveyRejectToServer();

      if (!Pollfish.helper.isRouterDisabled()) {
        Pollfish.helper.getAdditionalSurvey();
      } else {
        Pollfish.ui.noSurveyAvailable();
      }
    },

    sendSurveyRejectToServer() {
      let question;
      const questionElement = $('.js-question');
      const currentQuestionRefId = questionElement.data('ref-id');
      const questionId = questionElement.attr('id');
      // we prefer using refIds over ids but in some cases where a question might not have a refId
      if (currentQuestionRefId) {
        question = Pollfish.survey.getQuestionByRefId(currentQuestionRefId);
      } else {
        question = Pollfish.survey.getQuestionById(questionId);
        // throw a warning only if question does not contain a refId when comes for backend
        Raven.captureMessage(
          'Fallbacked to questionId when it should find a question by refId',
          {
            extra: {
              currentQuestionRefId,
              questionId,
              question,
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
      }
      const answerExists = $('.answerClick').length > 0;
      let answer;
      if (answerExists) {
        const answerId = $('.answerClick')
          .attr('id')
          .split('-')[0];
        answer = Pollfish.survey.getAnswerById(answerId, question);
      }
      const rawData = get(Pollfish, 'helper.json.raw') || {};
      // format data sent like we do for answer saved
      const rewardConversionHash = rawData.reward_conversion_hash || '';
      const surveyId = get(Pollfish, 'helper.json.s_id', '');
      const data = {
        s_id: surveyId.toString(),
        reward_name: rawData.reward_name
          ? encodeURIComponent(rawData.reward_name)
          : '',
        reward_conversion: rawData.reward_conversion || '',
        reward_conversion_hash: encodeURIComponent(rewardConversionHash),
        trigger_page: Pollfish.store.surveyRejectTriggerPage,
      };
      if (question && answer) {
        const questionRefId = question.refId || question.id;
        data.q_id = question.id;
        data.answers = [{ id: answer.id }];
        if (answer.style === ANSWER_OTHER) {
          // single choice vs open_ended feedback screen
          const text =
            $.trim($(`#otherContainerInput-${questionRefId}`).val()) ||
            $.trim($(`#freeText-${questionRefId}`).val());
          data.answers[0].text = encodeURIComponent(
            escapeJSONSpecialChars(text),
          );
        }
      }
      const noCheck = invoke(Pollfish, 'survey.getNoCheck');
      if (noCheck !== null) {
        data.no_check = noCheck;
      }

      Pollfish.helper.addRejectedSurvey(Pollfish.helper.json.s_id);
      const url = '/v2/device/set/survey/reject';
      return request({ url, method: 'POST', data });
    },

    sendSurveyCompletedToServer({
      isSessionFinished = false,
      isUserNotEligible,
    } = {}) {
      if (Pollfish.survey.isFeedbackSurvey()) {
        return Pollfish.helper.sendSurveyRejectToServer();
      }

      const surveyId = get(Pollfish, 'helper.json.s_id', '');
      if (!surveyId) {
        Raven.captureMessage(
          'Tried calling surveyCompleted without a survey_id',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
        return Promise.reject();
      }
      const knowledge = Pollfish.store.getRespondentKnowledge();
      let answers = Pollfish.survey.getStoredAnswers();
      answers = cleanAnswersFromUnusedProperties(answers);

      const data = {
        s_id: surveyId.toString(),
        distribution_id: Pollfish.survey.getDistributionId(),
        sanity_check: true,
        repeat_check: true,
        answers,
        attribute_grouping_combinations: transformConceptsForBackendStorage(
          knowledge.concepts,
        ),
        // NOTE: this is probably not used by the backend but is added here as a helper
        //       in case they need it in the future
        choice_set_block_ids: knowledge.choiceSetBlockIds,
        questionnaire_visibility: transformValuesToString(
          knowledge[KnowledgeKeys.Visibility],
        ),
      };

      const outcomeReason = Pollfish.surveyState.getFailedOutcomeReason();
      if (
        Pollfish.helper.isDistributionLink() &&
        Pollfish.helper.isDebugMode() &&
        outcomeReason
      ) {
        data.adlOutcome = outcomeReason;
      }

      const noCheck = invoke(Pollfish, 'survey.getNoCheck', null);
      if (noCheck !== null) {
        data.no_check = noCheck;
      }

      // get the current state/action respondent is into and send it to backend
      const surveyState = Pollfish.surveyState.getState();
      const url = `/v2/device/set/survey/completed?sessionFinished=${isSessionFinished}&state=${surveyState}`;
      const isFeedbackSurvey = get(Pollfish, 'survey.props.isFeedbackSurvey');
      if (
        !isFeedbackSurvey &&
        !Pollfish.helper.isDebugMode() &&
        !isUserNotEligible &&
        (!Pollfish.survey.getSurveySessionId() ||
          isEmpty(Pollfish.survey.getSurveySessionId()))
      ) {
        Raven.captureMessage(
          'Missing survey session id from survey finish event',
          {
            extra: {
              ...Pollfish.helper.getRedirectData(),
            },
          },
        );
      }
      return request({ url, method: 'POST', data });
    },

    async sendSessionFinishedToServer() {
      const rawData = get(Pollfish, 'helper.json.raw');
      const rewardConversionHash = rawData.reward_conversion_hash || '';
      const requestParams = {
        reward_name: rawData.reward_name
          ? encodeURIComponent(rawData.reward_name)
          : '',
        reward_conversion: rawData.reward_conversion || '',
        reward_conversion_hash: encodeURIComponent(rewardConversionHash),
      };
      if (Pollfish.helper.json.s_id) {
        requestParams.s_id = `${Pollfish.helper.json.s_id}`;
      }
      const url = `/v2/device/set/session/completed?hasError=${Pollfish.helper
        .hasError || false}`;
      try {
        await request({
          url,
          method: 'POST',
          data: requestParams,
        }).catch(noop);
      } catch (e) {
        if (e) {
          Raven.captureException(e);
        }
      }
      // we don't care about the response of the session finished, we only care that the
      // request happened and that it was successfully (or unsuccessfully) received.
      return true;
    },

    getInitialHeight() {
      Pollfish.ui.initialDimensions = {
        width: $(window).width(),
        height: $(window).height(),
      };

      return Pollfish.ui.initialDimensions;
    },

    listenForFocus() {
      window.onfocus = () => {
        if (Pollfish.helper.lastSurveyId) {
          Pollfish.helper.getMediationSurveyStatus(
            Pollfish.helper.lastSurveyId,
          );
        }
      };
    },

    removeFocusListeners() {
      window.onfocus = null;
    },

    getTextFromField(selector) {
      if (!selector) {
        return '';
      }
      let text = $.trim($(selector).val());
      // check if it is API, iOS and sdk version < 12 don't convert to BASE64
      if (
        jQuery.isEmptyObject(Pollfish.helper.html.json) &&
        !Pollfish.helper.isIOS()
      ) {
        // regex to check if non-latin
        const rforeign = /[^\u0000-\u007f]/; //eslint-disable-line

        // i am not sure if this is still happening. adding an analytics event
        // NOTE: happens twice a month or so
        if (rforeign.test(text) && Pollfish.helper.json.sdk_version < 12) {
          Pollfish.analytics.sendEvent(
            'shouldDeprecate',
            'base64Encode',
            Pollfish.helper.json.s_id,
          );
          text = `BASE64_${base64.encode(text)}`;
        }
      }
      return text;
    },

    rgbaToHex(orig) {
      let a;
      const rgb = orig
        .replace(/\s/g, '')
        .match(/^rgba?\((\d+),(\d+),(\d+),?([^,\s)]+)?/i);
      const alpha = ((rgb && rgb[4]) || '').trim();
      let hex = rgb
        ? (rgb[1] | (1 << 8)).toString(16).slice(1) + //eslint-disable-line
          (rgb[2] | (1 << 8)).toString(16).slice(1) + //eslint-disable-line
          (rgb[3] | (1 << 8)).toString(16).slice(1) //eslint-disable-line
        : orig;

      if (alpha !== '') {
        a = alpha;
      } else {
        a = '1';
      }
      // multiply before convert to HEX
      a = ((a * 255) | (1 << 8)).toString(16).slice(1); //eslint-disable-line
      hex += a;

      return hex;
    },

    getTextColor(rgbaOrHex) {
      let color = rgbaOrHex;
      if (color.indexOf('rgba') === 0) {
        color = Pollfish.helper.rgbaToHex(color);
      }
      if (rgbaOrHex.indexOf('#') === 0) {
        color = rgbaOrHex.slice(1);
      }
      // convert 3-digit hex to 6-digits.
      if (color.length === 3) {
        color = color[0] + color[0] + color[1] + color[1] + color[2] + color[2];
      }

      if (color.length !== 6 && color.length !== 8) {
        return '#fff';
      }
      const r = parseInt(color.slice(0, 2), 16);
      const g = parseInt(color.slice(2, 4), 16);
      const b = parseInt(color.slice(4, 6), 16);
      const a = (parseInt(color.slice(6, 8), 16) || 255) / 255;
      // http://stackoverflow.com/a/3943023/112731
      return (r * 0.299 + g * 0.587 + b * 0.114) * a > 186
        ? '#000000'
        : '#FFFFFF';
    },

    getQuestionGroup(question) {
      if (!has(question, 'groupRefId')) {
        return undefined;
      }
      const group = find(Pollfish.survey.groups, [
        'refId',
        question.groupRefId,
      ]);
      if (!group) {
        throw new Error('Expected a group but it was not found');
      }
      return group;
    },

    async getSurveyStatus({
      surveyId,
      maxWait = Infinity,
      onFirstCall,
      onProcessingStart,
    }) {
      let status = 'pending';
      const timeStarted = Date.now();
      let payload;
      let retries = 0;
      let hasTimedOut = false;
      const timeout = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
      let hasStartedProcessing = false;
      while (PENDING_PARTICIPATION_STATUSES.includes(status) && !hasTimedOut) {
        // in case we have rejected a survey from its feedback screen
        // but the server hasn't computed the result yet, then we will
        // continue fetching survey statuses indefenitely, which is not
        // good. Therefore we will cancel out on our own these requests
        // since we only expect a rejection in this case.
        if (Pollfish.helper.hasRejectedSurvey(surveyId)) {
          // if for any reason we get a participation status for an already
          // rejected survey we should still show the screen out page
          if (retries === 0) {
            const lastSessionData = get(
              Pollfish,
              'store.lastPayload.session',
              {},
            );
            return {
              status: 'screened_out',
              participationStatus: PARTICIPATION_STATUS.USER_REJECTION,
              // TODO: It's likely that this will result in a bug, as it isn't
              // so well thought out.
              // We need the last session data for rejected surveys so that we can
              // show reward info on the final screen.
              session: {
                ...lastSessionData,
              },
            };
          }
          // this just cancels pending requests
          return {
            status: 'canceled',
            participationStatus: PARTICIPATION_STATUS.USER_REJECTION,
          };
        }

        const data = { s_id: surveyId };
        const url = `/v2/surveys/${surveyId}/participation-status-live`;

        // eslint-disable-next-line no-await-in-loop
        payload = await request({ url, data });
        ({ status } = payload);
        const currentTime = Date.now();
        hasTimedOut = currentTime - timeStarted >= maxWait;
        if (PENDING_PARTICIPATION_STATUSES.includes(status) && !hasTimedOut) {
          // Waiting at least 1 second before asking the server again
          // eslint-disable-next-line no-await-in-loop
          await timeout(1000);
        }

        if (status === 'processing' && !hasStartedProcessing) {
          hasStartedProcessing = true;
          onProcessingStart(payload);
        }
        // only call this on the root request
        if (retries === 0 && isFunction(onFirstCall)) {
          onFirstCall(payload);
        }
        retries += 1;
      }

      if (!EXPECTED_PAYLOAD_STATUS_ENUMS.includes(status)) {
        const error = new Error(`Unexpected payload status ${payload.status}`);
        Raven.captureException(error, {
          extra: {
            ...payload,
            surveyId,
            device_id: get(Pollfish, 'helper.json.raw.device_id'),
          },
        });
        console.error(error); // eslint-disable-line
      }

      if (
        status !== 'pending' &&
        !EXPECTED_PARTICIPATION_STATUS_ENUMS.includes(
          payload.participationStatus,
        )
      ) {
        const error = new Error(
          `Unhandled participation status ${payload.participationStatus}`,
        );
        Raven.captureException(error, {
          extra: {
            ...payload,
            surveyId,
            device_id: get(Pollfish, 'helper.json.raw.device_id'),
          },
        });
        console.error(error); // eslint-disable-line
      }

      const metricTags = {
        status: payload.status,
        debug: Pollfish.helper.isDebugMode(),
        containsRedirectUrl: payload.customEndPage || false,
      };
      if (status === 'screened_out' || status === 'success') {
        metricTags.participationStatus = payload.participationStatus;
        metricTags.termReason = payload.termReason;
        if (!payload.participationStatus) {
          Raven.captureMessage('Missing participation status from payload', {
            extra: {
              ...payload,
              ...Pollfish.helper.getRedirectData(),
            },
          });
        }
      }
      Pollfish.analytics.sendMetric({
        metric: 'participation-status-monitor',
        deviceId: get(Pollfish, 'helper.json.raw.device_id', null),
        surveyId,
        tags: metricTags,
      });

      return payload;
    },

    getMediationSurveyStatus(surveyId, callback) {
      this.getSurveyStatus({
        surveyId,
        onFirstCall: callback,
        onProcessingStart: Pollfish.ui.showSpinner,
      })
        .then((payload) => {
          const reward = this.getRewardFromParticipation(payload);

          switch (payload.status) {
            case 'screened_out':
              if (
                Pollfish.helper.isRouterDisabled() ||
                Pollfish.helper.isDebugMode()
              ) {
                Pollfish.ui.showScreenOut({
                  reason: payload.participationStatus,
                  reward,
                  payload,
                  surveyId,
                });
              } else {
                // lastPayload is only needed/used for handling router requests
                // so it's safer to only add it here
                Pollfish.store.lastPayload = { ...payload, surveyId };
                Pollfish.helper.getAdditionalSurvey();
              }
              break;
            case 'success': {
              if (Pollfish.survey) {
                // It shows a feedback survey but it will refresh on the background as successful
                Pollfish.survey.setProps({ isFeedbackSurvey: false });
              }
              Pollfish.mobile.interface.setSurveyCompleted(payload);
              Pollfish.ui.thankYouBasic({
                isResultPending: false,
                surveyId,
                reward,
                payload,
              });
              break;
            }
            case 'canceled':
              // request canceled, do nothing
              break;
            default:
              Pollfish.ui.thankYouBasic();
              break;
          }
        })
        .catch((e) => {
          if (e) {
            Raven.captureException(e);
          }
        });

      Pollfish.helper.removeFocusListeners();
    },

    // We needed a way to distinguish between survey rejections on different _survey_ sessions
    // https://pollfish.atlassian.net/browse/PUB-2713 since there is a way to get
    // the same survey a second time, after the rejecting it the first
    // in some cases (like returning from a mediation provider)
    // the distribution_id param might be undefined. The hash still works though
    // because on the next router/refetching or whatever the distribution_id will exist
    addRejectedSurvey(surveyId) {
      const distributionId =
        invoke(Pollfish, 'survey.getDistributionId') ||
        get(Pollfish, 'helper.json.distribution_id');
      const rejectedSurveyHash = `${surveyId}-${distributionId}`;
      Pollfish.store.userRejectedSurveys.push(rejectedSurveyHash);
    },
    hasRejectedSurvey(surveyId) {
      const distributionId =
        invoke(Pollfish, 'survey.getDistributionId') ||
        get(Pollfish, 'helper.json.distribution_id');
      const rejectedSurveyHash = `${surveyId}-${distributionId}`;
      return (
        Pollfish.store.userRejectedSurveys.indexOf(rejectedSurveyHash) > -1
      );
    },
    getStylePropertyViewHeight() {
      return document.documentElement.style.getPropertyValue('--vh') || '100vh';
    },
    setStylePropertyViewHeight(vh) {
      document.documentElement.style.setProperty('--vh', vh);
    },
    // When opening a browser in a mobile device, the navigation bar
    // does not count as part of the vh causing the content to have scroll
    // when we use vh unit in css, check ".wrapper" class
    // this function will force ".wrapper" content to use the
    // remaining height of the screen height without the navigation bar
    calculateWindowHeight() {
      // First we get the viewport height and we multiple it by 1% to get a value for a vh unit
      const vh = window.innerHeight * 0.01;
      // Then we set the value in the --vh custom property to the root of the document
      Pollfish.helper.setStylePropertyViewHeight(`${vh * 100}px`);
    },
    shouldHideCloseButton() {
      return get(Pollfish, 'helper.json.raw.hide_x_button') === 'true';
    },
    shouldHideNoThanks() {
      return (
        Pollfish.helper.isDistributionLink() ||
        get(Pollfish, 'helper.json.raw.hide_no_thanks') === 'true'
      );
    },
    shouldHideHomescreen() {
      return get(Pollfish, 'helper.json.hideHomescreen', false);
    },
    shouldHideLogo() {
      const settings = Pollfish.helper.getSurveyTheme();
      if (!get(settings, 'advancedThemeSettingsEnabled', false)) {
        return false;
      }
      return get(settings, 'hideLogo', false);
    },
    whiteLabelEnabled() {
      return get(Pollfish, 'helper.json.whiteLabel', false);
    },
  };
})();

export default Pollfish.helper;
